import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';
import './UnitsAddScreen.css';

function UnitsAddScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unitTypes, setUnitTypes] = useState([]);
  const [cleaningZones, setCleaningZones] = useState([]);
  const [bookingEngines, setBookingEngines] = useState([]);
  
  const [formData, setFormData] = useState({
    unitNumber: '',
    unitTypeId: '',
    active: true,
    address: '',
    description: '',
    cleaningZone: '',
    bookingEngine: '',
    cleanStatus: 'Clean',
    sortOrder: 0,
    ownerPayCode: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchUnitTypes();
    fetchCleaningZones();
    fetchBookingEngines();
  }, []);

  const fetchUnitTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/unit-types`);
      if (response.ok) {
        const data = await response.json();
        setUnitTypes(data);
        if (data.length > 0) {
          setFormData(prev => ({ ...prev, unitTypeId: data[0].id }));
        }
      }
    } catch (error) {
      console.error('Error fetching unit types:', error);
    }
  };

  const fetchCleaningZones = async () => {
    try {
      const response = await fetch(`${API_URL}/cleaning-zones`);
      if (response.ok) {
        const data = await response.json();
        setCleaningZones(data);
      }
    } catch (error) {
      console.error('Error fetching cleaning zones:', error);
    }
  };

  const fetchBookingEngines = async () => {
    try {
      const response = await fetch(`${API_URL}/booking-engines`);
      if (response.ok) {
        const data = await response.json();
        setBookingEngines(data);
      }
    } catch (error) {
      console.error('Error fetching booking engines:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.unitNumber.trim()) {
      errors.unitNumber = 'Unit number is required';
    }
    
    if (!formData.unitTypeId) {
      errors.unitTypeId = 'Unit type is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const unitData = {
        unit_number: formData.unitNumber,
        unit_type_id: parseInt(formData.unitTypeId),
        active: formData.active,
        address: formData.address || null,
        description: formData.description || null,
        cleaning_zone: formData.cleaningZone || null,
        booking_engine: formData.bookingEngine || null,
        clean_status: formData.cleanStatus,
        sort_order: parseInt(formData.sortOrder) || 0,
        owner_pay_code: formData.ownerPayCode || null
      };
      
      const response = await fetch(`${API_URL}/units`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(unitData),
      });
      
      if (response.ok) {
        const result = await response.json();
        setSuccess(`Unit ${result.unit.unit_number} created successfully!`);
        
        // Reset form after successful creation
        setTimeout(() => {
          navigate('/dashboard/units-list');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error creating unit');
      }
    } catch (error) {
      console.error('Error creating unit:', error);
      setError('Error creating unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      unitNumber: '',
      unitTypeId: '',
      active: true,
      address: '',
      description: '',
      cleaningZone: '',
      bookingEngine: '',
      cleanStatus: 'Clean',
      sortOrder: 0,
      ownerPayCode: ''
    });
    setValidationErrors({});
    setError('');
    setSuccess('');
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const isFieldInvalid = (fieldName) => {
    return !!validationErrors[fieldName];
  };

  return (
    <Container fluid className="units-add-screen">
      <Row>
        <Col>
          <h1 className="mb-4">Add New Unit</h1>
          
          {/* Success Alert */}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          
          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="unitNumber"
                        value={formData.unitNumber}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('unitNumber')}
                        placeholder="Enter unit number"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('unitNumber')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit Type *</Form.Label>
                      <Form.Select
                        name="unitTypeId"
                        value={formData.unitTypeId}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('unitTypeId')}
                      >
                        <option value="">Select unit type</option>
                        {unitTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.unit_type}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('unitTypeId')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="active"
                        value={formData.active.toString()}
                        onChange={handleInputChange}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter unit description"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Address Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Address Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Enter unit address"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Operational Settings */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Operational Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cleaning Zone</Form.Label>
                      <Form.Select
                        name="cleaningZone"
                        value={formData.cleaningZone}
                        onChange={handleInputChange}
                      >
                        <option value="">No Zone</option>
                        {cleaningZones.map(zone => (
                          <option key={zone.id} value={zone.name}>
                            {zone.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Booking Engine</Form.Label>
                      <Form.Select
                        name="bookingEngine"
                        value={formData.bookingEngine}
                        onChange={handleInputChange}
                      >
                        <option value="">No Engine</option>
                        {bookingEngines.map(engine => (
                          <option key={engine.id} value={engine.name}>
                            {engine.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Clean Status</Form.Label>
                      <Form.Select
                        name="cleanStatus"
                        value={formData.cleanStatus}
                        onChange={handleInputChange}
                      >
                        <option value="Clean">Clean</option>
                        <option value="Dirty">Dirty</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Inspected">Inspected</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sort Order</Form.Label>
                      <Form.Control
                        type="number"
                        name="sortOrder"
                        value={formData.sortOrder}
                        onChange={handleInputChange}
                        placeholder="Enter sort order"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Owner Pay Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="ownerPayCode"
                        value={formData.ownerPayCode}
                        onChange={handleInputChange}
                        placeholder="Enter owner pay code"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Form Actions */}
            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex gap-3 justify-content-end">
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset Form
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline-secondary" 
                    onClick={() => navigate('/dashboard/units-list')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating Unit...
                      </>
                    ) : (
                      'Create Unit'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}

export default UnitsAddScreen;
